import { useSessionUser } from '@/hooks/use-session'
import { companyDirectoryOwnerId, effectiveCompanyRole } from 'shared/pure'
import type { CompanyPermission, EmployeeRole } from 'shared'

/**
 * The current actor's company-management role + permission checks. `can` simply tests membership in
 * `user.permissions` — the FULL set the SERVER resolved (role grants ∪ static `orgs:*` grant; see
 * resolveCompanyPermissions in shared) and shipped on getUser — so the client never recomputes a role
 * table and the backend (`companyProcedure`) enforces the exact same set. Gate company-management UI with
 * this so it never offers an action the backend would reject (an `authorized` employee sees read-only
 * chrome, a `collaborator` sees no company management at all). The backend stays the source of truth —
 * this is UX only; clicking a hidden-but-reachable action still gets an INSUFFICIENT_PERMISSIONS rejection.
 *
 * `role` is still derived here (not a permission, but needed for interface gating below): a company
 * account is always 'administrator'; an employee carries the ACTIVE employer's membership role; anyone
 * else (self-registered professional, or an employee on their personal D'Anchiano profile) has none.
 */
export function usePermissions(): {
  role: EmployeeRole | null
  isCompanyContext: boolean
  can: (permission: CompanyPermission) => boolean
  // Block C: render the COMPANY interface (a company account, or a promoted employee viewing 'company').
  isCompanyView: boolean
  // The current promoted-employee view (server-resolved + role-gated); 'employee' for everyone else.
  interfaceMode: 'employee' | 'company'
  // A promoted admin/authorized employee who can toggle between their employee profile and the company view.
  canToggleInterface: boolean
  // Whether this actor reaches a company's DIRECTORY — its people, puestos, ofertas, candidatos and the
  // "Ordenar colectivos por ajuste" collectives. Distinct from `isCompanyView`, which is about which
  // interface is on screen: an admin/authorized employee compares against the company's whole directory
  // from their own "Mi perfil" too, so gating the compare dropdown on the VIEW shrank it to a
  // collaborator's list the moment they switched back (spec: Autorizado/Administrador compare). Equipos
  // are the exception and stay per-team restricted, which the server enforces. Read-only vs. writable is
  // a separate question — that one is `can(...)`, and it is where the two roles actually differ.
  canBrowseCompanyDirectory: boolean
} {
  const user = useSessionUser()

  const role: EmployeeRole | null = user ? effectiveCompanyRole(user) : null

  const isEmployee = user?.type === 'applicant' && user.isEmployee
  const interfaceMode = user?.interfaceMode ?? 'employee'
  // A promoted admin/authorized employee (NOT the principal admin's self-employee — that one switches
  // identity via adminOfCompanyId, handled separately) may switch between the two views.
  const canToggleInterface =
    isEmployee && user?.activeCompanyId != null && (role === 'administrator' || role === 'authorized')

  return {
    role,
    // Operating inside a company context: a company account, or an employee acting under an employer.
    isCompanyContext: role != null,
    // Derived by the SAME shared function the server gates on (companyDirectoryOwnerId), never
    // re-derived from the role here — otherwise the two answers drift and the dropdown starts offering
    // what the selectors won't serve. Deliberately NOT `canToggleInterface`: that predicate answers a
    // different question (may they switch views) and would silently redefine reach if it ever changed.
    canBrowseCompanyDirectory: user != null && companyDirectoryOwnerId(user) != null,
    can: (permission: CompanyPermission) => user?.permissions?.includes(permission) ?? false,
    isCompanyView: user?.type === 'company' || (isEmployee && interfaceMode === 'company'),
    interfaceMode,
    canToggleInterface,
  }
}
